import React, { useMemo } from 'react';
import { Column, Row, Title } from '@ydistri/ds';
import {
  GetCategoryInsightsParams,
  useGetCategoryInsightsQuery,
} from '../../../../components/global/CategoryInsights/apiCategoryInsights';
import { Spin } from 'antd';
import { computeAnnualRevenueInStockPrice, computeTurnover } from '@ydistri/utils';
import { useCurrency } from '../../../../hooks/useCurrency';
import CategoryInsightsContributionBar from '../SegmentPotentialDefinition/components/CategoryInsightsContributionBar';
import CategoryInsightsMetric from '../SegmentPotentialDefinition/components/CategoryInsightsMetric';
import MarginDescription from '../../components/MarginDescription';
import { computePercentage, formatPercentage } from '@ydistri/generic-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';
import { ROOT_CATEGORY_ID } from '../../../../components/global/_constants';
import useCategoryId from '../../../../hooks/useCategoryId';

interface CategorySummary {
  availableSupply: number;
  availableSupplyPercentage: number;
  annualRevenue: number;
  annualRevenuePercentage: number;
  margin: number;
  turnover: number;
}

const SegmentCategorySummary: React.FC = () => {
  const categoryId = useCategoryId();
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const currency = useCurrency();

  const categoryInsightsParam: GetCategoryInsightsParams = useMemo(
    () => ({
      categoryId,
      templateOrCalculation,
    }),
    [categoryId, templateOrCalculation],
  );

  const rootCategoryInsightsParam: GetCategoryInsightsParams = useMemo(
    () => ({
      categoryId: ROOT_CATEGORY_ID,
      templateOrCalculation,
    }),
    [templateOrCalculation],
  );

  const { data: rootCategoryData, isFetching: isRootCategoryInsightsLoading } =
    useGetCategoryInsightsQuery(
      templateOrCalculation.hasValidValue ? rootCategoryInsightsParam : skipToken,
    );
  const { data: categoryData, isFetching: isCategoryInsightsLoading } = useGetCategoryInsightsQuery(
    templateOrCalculation.hasValidValue ? categoryInsightsParam : skipToken,
  );

  const data: CategorySummary = useMemo(() => {
    const rootAvailableSupply = rootCategoryData?.attributes.AvailableSupplyValue ?? 0;
    const rootAnnualRevenue = rootCategoryData?.attributes.AnnualRevenueSalePriceValue ?? 0;

    const availableSupply = categoryData?.attributes.AvailableSupplyValue ?? 0;
    const annualRevenue = categoryData?.attributes.AnnualRevenueSalePriceValue ?? 0;
    const margin = categoryData?.attributes.Margin ?? 0;

    const annualRevenueInStockPrice = computeAnnualRevenueInStockPrice(annualRevenue, margin);
    const turnover = computeTurnover(annualRevenueInStockPrice, availableSupply);

    const availableSupplyPercentage = computePercentage(availableSupply, rootAvailableSupply, 2);
    const annualRevenuePercentage = computePercentage(annualRevenue, rootAnnualRevenue, 2);

    return {
      availableSupply,
      availableSupplyPercentage,
      annualRevenue,
      annualRevenuePercentage,
      margin,
      turnover,
    };
  }, [
    rootCategoryData?.attributes.AvailableSupplyValue,
    rootCategoryData?.attributes.AnnualRevenueSalePriceValue,
    categoryData?.attributes.AvailableSupplyValue,
    categoryData?.attributes.AnnualRevenueSalePriceValue,
    categoryData?.attributes.Margin,
  ]);

  return (
    <Column $gap="16px" data-testid="segment-category-summary" $flexGrow={0}>
      <Title $size="large">Category insights</Title>
      <Spin spinning={isCategoryInsightsLoading || isRootCategoryInsightsLoading}>
        <Row $gap="2rem" $justifyContent="space-between" $padding={0}>
          <Row $flexGrow={3} $alignSelf="stretch" $gap="2rem" $padding={0} $flexWrap="nowrap">
            <CategoryInsightsContributionBar
              value={data.availableSupply}
              percentage={data.availableSupplyPercentage}
              description={`inventory (${currency})`}
            />
            <CategoryInsightsContributionBar
              value={data.annualRevenue}
              percentage={data.annualRevenuePercentage}
              description={`annual revenue (${currency})`}
            />
          </Row>
          <Column $flexGrow={1} $gap="1rem">
            <CategoryInsightsMetric
              name="turnover"
              formattedValue={`${data.turnover} month`}
              tooltipContent="Turnover = Available Supply in cost price / (Annual Cost of Goods Sold / 12 months)"
            />
            <CategoryInsightsMetric
              name="margin"
              formattedValue={formatPercentage(data.margin * 100, 1)}
              tooltipContent={<MarginDescription />}
            />
          </Column>
        </Row>
      </Spin>
    </Column>
  );
};

export default SegmentCategorySummary;
